import { Component, HostListener, Input, OnDestroy, OnInit } from '@angular/core';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { Store } from '@ngrx/store';
import { Subscription, Observable } from 'rxjs';
import { EDeviceCategory } from 'src/app/conf/device-categories';
import { EDeviceModel } from 'src/app/conf/device-models';
import { Device, DeviceTechnicianRole } from 'src/app/model/device';
import { NotifyAdvanceConfig } from 'src/app/model/mail-notification-config';
import * as EventActions from 'src/app/store/event/event.actions';

import { AppState } from 'src/app/store/app.reducer';
import { selectCurrentDeviceStatus } from 'src/app/store/device/device.selector';
import { DeviceStatus } from 'src/app/model/device-status';
import { StandardNewValidator, StandardPageLayout } from 'src/app/modules/shared/stdlayoutlib/classes.stdlayoutlib';
import { selectListNotifyConfig, selectLoadingListNotifyConfig } from 'src/app/store/event/event.selector';
import { EEventIds } from 'src/app/conf/events';
import { TranslateService } from '@ngx-translate/core';
import { User } from 'src/app/model/user';
import { selectUser } from 'src/app/store/user/user.selector';
import { ERoles } from 'src/app/model/role';

@Component({
	selector: 'app-notifications-device-list',
	templateUrl: './notifications-device-list.component.html',
	providers: [],
})

export class NotificationsDeviceListComponent implements OnInit, OnDestroy {
	@Input() device: Device;
	@Input() fromTechnicianDevicesManagement: boolean;
	@Input() supportReqTechnician: DeviceTechnicianRole; // 1: Open, 0: Close, 2: TechnicianOwner

	deviceStatus$: Observable<DeviceStatus | undefined>;
	deviceStatusSub: Subscription | undefined;
	listNotifyConfig$: Observable<NotifyAdvanceConfig[] | undefined>;
	listNotifyConfigSub: Subscription | undefined;

	notificationsConfigsForm: FormGroup;

	listNotifyConfig: NotifyAdvanceConfig[];
	loadingListNotifyConfig: boolean = false;
	loadingListNotifyConfig$: Observable<boolean | undefined>;
	loadingListNotifyConfigSub: Subscription | undefined;

	configured: boolean;
	online: boolean;

	iMTechOwner: boolean;

	currentUser: User;
	currentUser$: Observable<User | undefined>;
	currentUserSub: Subscription | undefined;

	// design
	screenWidth: number;
	MIN_MD_WIDTH = 500;

	constructor(
		private store: Store<AppState>,
		private fb: FormBuilder,
		private translate: TranslateService,
	) {
		this.initSelectors();
	}

	ngOnInit(): void {
		this.store.dispatch(EventActions.getNotifyAdvanceDevice({ deviceId: this.device.Id, ownerId: this.device.OwnerId }));
		this.subscribeAll();
		StandardNewValidator.setFirstCompile(true);
		this.onResize();
	}

	initSelectors(): void {
		this.deviceStatus$ = this.store.select(selectCurrentDeviceStatus);
		this.listNotifyConfig$ = this.store.select(selectListNotifyConfig);
		this.loadingListNotifyConfig$ = this.store.select(selectLoadingListNotifyConfig);
		this.currentUser$ = this.store.select(selectUser);
	}

	subscribeAll() {
		this.listNotifyConfigSub = this.listNotifyConfig$.subscribe(listNotifyConfig => {
			this.listNotifyConfig = this.convertList(listNotifyConfig);
			this.createForm();
			this.initializeForm();
		});
		this.deviceStatusSub = this.deviceStatus$.subscribe(deviceState => {
			if (deviceState) {
				this.configured = deviceState.Configured === 0;
				this.online = deviceState.Online;
			}
		});
		this.loadingListNotifyConfigSub = this.loadingListNotifyConfig$?.subscribe(loadingListNotifyConfig => {
			this.loadingListNotifyConfig = loadingListNotifyConfig;
		});
		this.currentUserSub = this.currentUser$.subscribe(currentUser => {
			if (currentUser) {
				this.currentUser = currentUser;
			}
		});
	}

	ngOnDestroy(): void {
		this.listNotifyConfigSub?.unsubscribe();
		this.deviceStatusSub?.unsubscribe();
		this.currentUserSub?.unsubscribe();
		this.loadingListNotifyConfigSub?.unsubscribe();
	}

	initializeForm() {
		// Manage view for technician owner device
		if (this.fromTechnicianDevicesManagement) {
			this.iMTechOwner = this.supportReqTechnician === DeviceTechnicianRole.TECH_OWNER;
		} else {
			const iMTechRole: boolean = this.currentUser?.Roles[0].Description === ERoles.Technician;
			this.iMTechOwner = iMTechRole && this.device.OwnerId === this.currentUser.Id;
		}
		this.listNotifyConfig.forEach(notify => {
			// CUSTOM EMAIL
			// if (!(notify.CustomEnabled)) { // switch off
			// 	this.f[notify.EventId + '-' + notify.TerminalId + '-CustomRecipient'].clearValidators();
			// 	this.f[notify.EventId + '-' + notify.TerminalId + '-CustomRecipient'].disable();
			// } else { // switch on
				// this.f[notify.EventId + '-' + notify.TerminalId + '-CustomRecipient'].setValidators([Validators.required, Validators.email]);
				// this.f[notify.EventId + '-' + notify.TerminalId + '-CustomRecipient'].enable();
			// }
			// this.f[notify.EventId + '-' + notify.TerminalId + '-CustomRecipient'].updateValueAndValidity();

			const userCheckbox = this.f[notify.EventId + '-' + notify.TerminalId + '-OwnerEnabled'];
			const technicianCheckbox = this.f[notify.EventId + '-' + notify.TerminalId + '-TechnicianEnabled'];
			const customMailCheckbox = this.f[notify.EventId + '-' + notify.TerminalId + '-CustomEnabled'];
			// FROM TECHNICIAN DEVICE MANAGEMENT
			if (this.fromTechnicianDevicesManagement) {
				// TECHNICIAN SUPP_REQ_CLOSED
				if (this.supportReqTechnician === DeviceTechnicianRole.SUP_REQ_CLOSED) {
					userCheckbox.disable();
					if (technicianCheckbox.value === true) {
						technicianCheckbox.enable();
					} else {
						technicianCheckbox.disable();
					}
					customMailCheckbox.disable();
					this.f[notify.EventId + '-' + notify.TerminalId + '-CustomRecipient'].clearValidators();
					this.f[notify.EventId + '-' + notify.TerminalId + '-CustomRecipient'].disable();
					this.f[notify.EventId + '-' + notify.TerminalId + '-CustomRecipient'].updateValueAndValidity();
				}
				// TECHNICIAN SUPP_REQ_OPENED && OWNER -> All enable()
			}
			// FROM DEVICE SETTING you are TECH SUPP_REQ_OPENED or OWNER so everything is enable()
		});
	}

	convertList(notifyList: NotifyAdvanceConfig[]): NotifyAdvanceConfig[] {
		if (notifyList !== undefined && notifyList !== null) {
			const list = [];
			for (const not of notifyList) {
				switch (not.EventId) {
					case EEventIds.DeviceError:
						not.Name = this.translate.instant('NTF_ERROR');
						list.push(not);
						break;
					case EEventIds.DeviceOnline:
						not.Name = this.translate.instant('NTF_WARNING');
						list.push(not);
						break;
					case EEventIds.DeviceInput:
						not.Name = this.createNameNotify(not);
						list.push(not);
						break;
					case EEventIds.DeviceOutput:
						not.Name = this.createNameNotify(not);
						list.push(not);
						break;
					default:
						not.Name = this.createNameNotify(not);
				}
			}
			return list;
		}
		return [];
	}

	createNameNotify(notify: NotifyAdvanceConfig): string {
		if (notify.Description !== undefined && notify.Description !== notify.DefaultDescription) {
			return this.translateData(notify.DefaultDescription) + ' (' + notify.Description + ')';
		}
		return this.translateData(notify.DefaultDescription);
	}

	translateData(str: string): string {
		let resStr = '';
		const fields = str.split('{');
		for (const field of fields) {
			if (field.indexOf('}') >= 0) {
				const toTranslate = field.split('}');
				resStr = resStr + this.translate.instant(toTranslate[0]) + toTranslate[1];
			} else {
				resStr = resStr + field;
			}
		}
		return resStr;
	}

	createForm(): void {
		this.notificationsConfigsForm = this.fb.group(this.buildForm());
	}

	buildForm() {
		const res = {};
		this.listNotifyConfig.forEach((notify) => {
			res[notify.EventId + '-' + notify.TerminalId + '-OwnerEnabled'] = [notify.OwnerEnabled];
			res[notify.EventId + '-' + notify.TerminalId + '-TechnicianEnabled'] = [notify.TechnicianEnabled];
			res[notify.EventId + '-' + notify.TerminalId + '-CustomEnabled'] = [notify.CustomEnabled];
			res[notify.EventId + '-' + notify.TerminalId + '-CustomRecipient'] = [notify.CustomRecipient];
		});
		return res;
	}

	supportInfo(): boolean {
		return this.device?.CategoryId === EDeviceCategory.Virtual || this.device?.ModelId === EDeviceModel.RBE4 ||
			this.device?.ModelId === EDeviceModel.RBE4030 || this.device?.ModelId === EDeviceModel.RBE4230 ||
			this.device?.ModelId === EDeviceModel.GEN_RBE4;
	}

	supportWarning(): boolean {
		return this.device?.CategoryId === EDeviceCategory.Automation &&
			this.device?.ModelId !== EDeviceModel.IORS485 && this.device?.ModelId !== EDeviceModel.GEN_IORS485;
	}

	supportError(): boolean {
		return (this.device?.CategoryId === EDeviceCategory.Automation) &&
			([EDeviceModel.RBE4, EDeviceModel.RBE4030, EDeviceModel.RBE4230, EDeviceModel.GEN_RBE4,
			EDeviceModel.RBE230, EDeviceModel.RBE24, EDeviceModel.RBE24230, EDeviceModel.GEN_RBE24230,
			EDeviceModel.IORS485, EDeviceModel.GEN_IORS485].includes(this.device?.ModelId) === false);
	}

	disableSaveButtonMail(notify: NotifyAdvanceConfig): boolean {
		return (this.supportReqTechnician === DeviceTechnicianRole.SUP_REQ_CLOSED || this.f[notify.EventId + '-' + notify.TerminalId + '-CustomRecipient'].pristine);
	}

	disableRemoveButtonMail(notify: NotifyAdvanceConfig): boolean {
		return (this.supportReqTechnician === DeviceTechnicianRole.SUP_REQ_CLOSED || !(this.f[notify.EventId + '-' + notify.TerminalId + '-CustomEnabled'].value));
	}

	// switchMailCheckbox(event: CheckboxChangeEvent, notify: NotifyAdvanceConfig) {
	// 	const checkboxValue = event.checked;
	// 	if (!checkboxValue) { // switch off
	// 		this.f[notify.EventId + '-' + notify.TerminalId + '-CustomRecipient'].clearValidators();
	// 		this.f[notify.EventId + '-' + notify.TerminalId + '-CustomRecipient'].disable();
	// 		this.f[notify.EventId + '-' + notify.TerminalId + '-CustomRecipient'].updateValueAndValidity();
	// 		this.saveNotify(notify); // i save switchoff email
	// 	} else { // switch on
	// 		this.f[notify.EventId + '-' + notify.TerminalId + '-CustomRecipient'].setValidators([Validators.required, Validators.email]);
	// 		this.f[notify.EventId + '-' + notify.TerminalId + '-CustomRecipient'].enable();
	// 		this.f[notify.EventId + '-' + notify.TerminalId + '-CustomRecipient'].updateValueAndValidity();
	// 	}
	// }

	remove(notify: any): void {
		StandardNewValidator.setFirstCompile(false);
		this.f[notify.EventId + '-' + notify.TerminalId + '-CustomRecipient'].clearValidators();
		this.f[notify.EventId + '-' + notify.TerminalId + '-CustomRecipient'].enable();
		this.f[notify.EventId + '-' + notify.TerminalId + '-CustomRecipient'].updateValueAndValidity();
		if (this.f[notify.EventId + '-' + notify.TerminalId + '-CustomRecipient'].errors === null) {
			this.saveNotify(notify, 'deleting');
		}
	}

	submit(notify: any): void {
		StandardNewValidator.setFirstCompile(false);
		this.f[notify.EventId + '-' + notify.TerminalId + '-CustomRecipient'].setValidators([Validators.required, Validators.email]);
		this.f[notify.EventId + '-' + notify.TerminalId + '-CustomRecipient'].enable();
		this.f[notify.EventId + '-' + notify.TerminalId + '-CustomRecipient'].updateValueAndValidity();
		if (this.f[notify.EventId + '-' + notify.TerminalId + '-CustomRecipient'].errors === null) {
			this.saveNotify(notify, 'saving');
		}
	}

	saveNotify(notify: NotifyAdvanceConfig, action: string): void {
		const notifyEdit = new NotifyAdvanceConfig(notify.EventId, notify.TerminalId,
			this.f[notify.EventId + '-' + notify.TerminalId + '-OwnerEnabled'].value, this.f[notify.EventId + '-' + notify.TerminalId + '-TechnicianEnabled'].value,
			this.f[notify.EventId + '-' + notify.TerminalId + '-CustomEnabled'].value, this.f[notify.EventId + '-' + notify.TerminalId + '-CustomRecipient'].value
		);
		if (action === 'deleting') { // deleting CustomEnabled and CustomRecipient
			notifyEdit.CustomEnabled = false;
			notifyEdit.CustomRecipient = null;
		} else { // adding User, Tech, CustomEnabled / CustomRecipient
			if (notifyEdit.CustomRecipient !== '' && notifyEdit.CustomRecipient !== null) { // i can adding tech or user without CustomEnabled
				notifyEdit.CustomEnabled = true;
			}
		}
		if (this.supportReqTechnician === DeviceTechnicianRole.SUP_REQ_CLOSED) { // Im a tech with RdS close, i can just remove notification for myself
			this.store.dispatch(EventActions.editNotifyAdvanceDevice({ deviceId: this.device.Id, notify: notifyEdit, ownerId: null }));
		} else { // RdS Open or Tech owner (i can do anything)
			this.store.dispatch(EventActions.editNotifyAdvanceDevice({ deviceId: this.device.Id, notify: notifyEdit, ownerId: this.device.OwnerId }));
		}
	}

	get f(): any {
		return this.notificationsConfigsForm.controls;
	}

	// Design
	@HostListener('window:resize')
	onResize(): void {
		this.screenWidth = window.innerWidth;
	}

	onExtendedMobile() {
		return StandardPageLayout.onExtendedMobile(this.screenWidth);
	}

	col6Responsive(): string {
		return StandardPageLayout.col6Responsive(this.screenWidth);
	}

	onTablet() {
		return StandardPageLayout.onTablet(this.screenWidth);
	}

	customPaddingX(): string {
		return StandardPageLayout.customPaddingX(this.screenWidth);
	}

	showErrorRequiredCheckBox(type: NotificationsDeviceListComponent["f"]): string {
		return StandardNewValidator.showErrorRequiredCheckBox(type);
	}

	manageRespStylewithError(type: NotificationsDeviceListComponent["f"], classType: string): string {
		return StandardNewValidator.manageRespStylewithError(type, classType, this.screenWidth);
	}

	dynamicInputTextDesktopTabletWidth(): string {
		return StandardPageLayout.dynamicInputTextDesktopTabletWidth(this.screenWidth, 'w-20rem', 'w-10rem');
	}

	simpleInputTextLayout(classType: string): string {
		return StandardPageLayout.simpleInputTextLayout(classType, this.screenWidth);
	}

	justifyContentStyle(): string {
		return StandardNewValidator.justifyMessagesNewValidator(this.screenWidth);
	}

	newShowErrors(notify: any, classType: string) {
		const type = this.f[notify.EventId+ '-' + notify.TerminalId + '-CustomRecipient'];
		return StandardNewValidator.newShowErrors(type, classType, this.screenWidth);
	}

	showErrorRequiredMessage(notify: any): boolean {
		const type = this.f[notify.EventId+ '-' + notify.TerminalId + '-CustomRecipient'];
		return StandardNewValidator.showErrorRequiredMessage(type);
	}
}
